// NEW: UI_REVAMP
import { useCallback, useEffect, useState } from 'react';
import { useBet3Store } from '../stores/useBet3Store';

export const SPECIAL_KEYS = {
	EMPTY: 'EMPTY',
	ZERO: '0',
	BACKSPACE: 'BACKSPACE',
};

export const useKeypad = (slotCount, { maxDigits = 1, onAddToList, onManualInteract } = {}) => {
	const slots = useBet3Store((state) => state.slots);
	const updateSlotStore = useBet3Store((state) => state.updateSlot);
	const setSlots = useBet3Store((state) => state.setSlots);

	const [activeSlot, setActiveSlot] = useState(0);

	const moveNext = useCallback(() => {
		setActiveSlot((prev) => Math.min(prev + 1, slotCount - 1));
	}, [slotCount]);

	const movePrev = useCallback(() => {
		setActiveSlot((prev) => Math.max(prev - 1, 0));
	}, []);

	const handleNumberClick = useCallback(
		(num) => {
			const currentValue = slots[activeSlot] || '';

			onManualInteract?.();
			if (currentValue.length >= maxDigits) {
				moveNext();
				return;
			}

			const newValue = maxDigits === 1 ? num : currentValue + num;
			updateSlotStore(activeSlot, newValue);

			if (newValue.length >= maxDigits) {
				if (activeSlot === slotCount - 1 && onAddToList && maxDigits === 1) {
					// Auto-add for 3D (slotCount 3, maxDigits 1)
					// Wait a bit for the last digit to be visible in state
					setTimeout(() => {
						onAddToList();
					}, 100);
				} else {
					moveNext();
				}
			}
		},
		[
			activeSlot,
			slots,
			updateSlotStore,
			moveNext,
			maxDigits,
			slotCount,
			onAddToList,
			onManualInteract,
		],
	);

	const handleBackspace = useCallback(() => {
		const currentValue = slots[activeSlot] || '';

		onManualInteract?.();
		if (currentValue.length > 0) {
			updateSlotStore(activeSlot, currentValue.slice(0, -1));
			return;
		}

		if (activeSlot > 0) {
			const prevIndex = activeSlot - 1;
			const prevValue = slots[prevIndex] || '';

			if (prevValue.length > 0) {
				updateSlotStore(prevIndex, prevValue.slice(0, -1));
			}

			movePrev();
		}
	}, [activeSlot, slots, updateSlotStore, movePrev, onManualInteract]);

	const onSlotClick = useCallback((index) => {
		setActiveSlot(index);
	}, []);

	const KEY_ACTIONS = {
		Backspace: (e) => {
			e.preventDefault();
			handleBackspace();
		},
		ArrowLeft: (e) => {
			e.preventDefault();
			movePrev();
		},
		ArrowRight: (e) => {
			e.preventDefault();
			moveNext();
		},
	};

	const handleKeyDown = useCallback(
		(e, index) => {
			if (index !== activeSlot) return;

			const action = KEY_ACTIONS[e.key];
			if (action) {
				action(e);
				return;
			}

			if (/^[0-9]$/.test(e.key)) {
				e.preventDefault();
				handleNumberClick(e.key);
				return;
			}

			e.preventDefault();
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[activeSlot, handleNumberClick, handleBackspace, moveNext, movePrev],
	);

	useEffect(() => {
		if (slots.length > 0 && slots.every((s) => s === '')) {
			setActiveSlot(0);
		}
	}, [slots]);

	useEffect(() => {
		if (slots.length !== slotCount) {
			setSlots(new Array(slotCount).fill(''));
		}
	}, [slotCount, setSlots, slots.length]);

	return {
		slots,
		activeSlot,
		setActiveSlot,
		handleNumberClick,
		handleBackspace,
		onSlotClick,
		handleKeyDown,
	};
};
